/*
  Emm's Plant House — Dynamic Plant Catalogue Service
  File: assets/emms-plant-catalogue-service.js
  Version: 1.0.0

  Purpose:
  - Fetch the complete plant_profile_core catalogue through Shopify Storefront API pagination.
  - Provide one reusable index for Master Care Guide, Soil Tool, Flowering Tool, Resolver and future tools.
  - Avoid hard-coded plant arrays and Liquid-rendered 250-entry caps.
*/
(function () {
  'use strict';

  if (window.EmmsPlantCatalogue && window.EmmsPlantCatalogue.version) return;

  var VERSION = '1.0.0';
  var DEFAULT_CONFIG = {
    shopDomain: '',
    storefrontApiVersion: '2026-04',
    storefrontAccessToken: '',
    plantCoreType: 'plant_profile_core',
    catalogueCacheVersion: 'v1',
    catalogueCacheTtlMs: 2 * 60 * 60 * 1000,
    pageSize: 250,
    maxPages: 30,
    debug: false
  };

  var config = Object.assign({}, DEFAULT_CONFIG, window.EmmsPlantToolsConfig || {});
  var memory = {
    promise: null,
    detailPromises: {},
    plants: null,
    byHandle: null,
    lastFetchedAt: null,
    source: 'unloaded'
  };

  var CATALOGUE_QUERY = `
    query EmmsPlantCoreCatalogue($type: String!, $first: Int!, $after: String) {
      metaobjects(type: $type, first: $first, after: $after, sortKey: "updated_at") {
        pageInfo { hasNextPage endCursor }
        edges {
          cursor
          node {
            id
            handle
            type
            updatedAt
            onlineStoreUrl
            fields { key type value }
          }
        }
      }
    }
  `;

  var DETAIL_QUERY = `
    query EmmsPlantCoreDetail($handle: MetaobjectHandleInput!) {
      metaobject(handle: $handle) {
        id
        handle
        type
        updatedAt
        onlineStoreUrl
        fields {
          key
          type
          value
          reference {
            __typename
            ... on Metaobject {
              id
              handle
              type
              onlineStoreUrl
              field(key: "plant_name") { value }
              problemName: field(key: "problem_name") { value }
              displayName: field(key: "display_name") { value }
              title: field(key: "title") { value }
            }
            ... on Product {
              id
              handle
              title
              onlineStoreUrl
              availableForSale
              featuredImage { url altText width height }
              priceRange { minVariantPrice { amount currencyCode } }
            }
            ... on MediaImage {
              id
              image { url altText width height }
            }
          }
          references(first: 30) {
            nodes {
              __typename
              ... on Metaobject {
                id
                handle
                type
                onlineStoreUrl
                field(key: "plant_name") { value }
                problemName: field(key: "problem_name") { value }
                displayName: field(key: "display_name") { value }
                title: field(key: "title") { value }
              }
              ... on Product {
                id
                handle
                title
                onlineStoreUrl
                availableForSale
                featuredImage { url altText width height }
                priceRange { minVariantPrice { amount currencyCode } }
              }
              ... on MediaImage {
                id
                image { url altText width height }
              }
            }
          }
        }
      }
    }
  `;

  function debug() {
    if (config.debug && window.console) console.log.apply(console, ['[EmmsPlantCatalogue]'].concat([].slice.call(arguments)));
  }

  function configure(nextConfig) {
    config = Object.assign({}, config, nextConfig || {});
    return Object.assign({}, config);
  }

  function endpoint() {
    if (config.storefrontEndpoint) return config.storefrontEndpoint;
    var domain = String(config.shopDomain || '').replace(/^https?:\/\//, '').replace(/\/$/, '');
    if (!domain) throw new Error('Missing Shopify shop domain for EmmsPlantCatalogue.');
    return 'https://' + domain + '/api/' + config.storefrontApiVersion + '/graphql.json';
  }

  function cacheKey() {
    return [
      'emms:plant-catalogue',
      config.shopDomain || 'shop',
      config.storefrontApiVersion,
      config.plantCoreType,
      config.catalogueCacheVersion
    ].join(':');
  }

  function canUseCache() {
    try { return !!window.localStorage; }
    catch (err) { return false; }
  }

  function readCache() {
    if (!canUseCache()) return null;
    if (new URLSearchParams(window.location.search || '').get('emmsCatalogueRefresh') === '1') return null;
    try {
      var raw = window.localStorage.getItem(cacheKey());
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.plants)) return null;
      if (!parsed.fetchedAt || Date.now() - parsed.fetchedAt > Number(config.catalogueCacheTtlMs)) return null;
      return parsed;
    } catch (err) {
      return null;
    }
  }

  function writeCache(plants) {
    if (!canUseCache()) return;
    try {
      window.localStorage.setItem(cacheKey(), JSON.stringify({ fetchedAt: Date.now(), plants: plants }));
    } catch (err) {
      debug('Could not write cache', err);
    }
  }

  function clearCache() {
    if (!canUseCache()) return;
    try { window.localStorage.removeItem(cacheKey()); }
    catch (err) { /* noop */ }
  }

  function graphQL(query, variables) {
    if (!config.storefrontAccessToken || String(config.storefrontAccessToken).indexOf('PASTE_') === 0) {
      return Promise.reject(new Error('Missing public Storefront API access token. Add it to the section setting or EmmsPlantToolsConfig.'));
    }

    return fetch(endpoint(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': config.storefrontAccessToken
      },
      body: JSON.stringify({ query: query, variables: variables || {} })
    }).then(function (res) {
      return res.json().then(function (json) {
        if (!res.ok || json.errors) {
          var message = json.errors ? json.errors.map(function (e) { return e.message; }).join('; ') : res.statusText;
          throw new Error(message || 'Storefront API request failed.');
        }
        return json.data;
      });
    });
  }

  function fieldMap(fields) {
    var out = {};
    (fields || []).forEach(function (field) {
      if (!field || !field.key) return;
      out[field.key] = field;
    });
    return out;
  }

  function fieldValue(map, keys) {
    for (var i = 0; i < keys.length; i += 1) {
      var field = map[keys[i]];
      if (field && field.value != null && String(field.value).trim() !== '') return String(field.value).trim();
    }
    return '';
  }

  function parseListValue(value) {
    if (!value) return [];
    var raw = String(value).trim();
    if (!raw) return [];
    try {
      var parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.filter(Boolean).map(String);
      if (parsed && typeof parsed === 'object') return Object.values(parsed).filter(Boolean).map(String);
    } catch (err) {
      // continue to comma split
    }
    return raw.split(/[,|]/).map(function (x) { return x.trim(); }).filter(Boolean);
  }

  function stripHtml(value) {
    var div = document.createElement('div');
    div.innerHTML = value || '';
    return (div.textContent || div.innerText || '').replace(/\s+/g, ' ').trim();
  }

  function inferGroup(name, botanical) {
    var text = (botanical || name || '').trim();
    if (!text) return 'Other houseplants';
    var first = text.split(/\s+/)[0].replace(/[^A-Za-z]/g, '');
    if (!first) return 'Other houseplants';

    var known = {
      hoya: 'Hoya', anthurium: 'Anthurium', alocasia: 'Alocasia', monstera: 'Monstera', philodendron: 'Philodendron',
      begonia: 'Begonia', ficus: 'Ficus', peperomia: 'Peperomia', calathea: 'Prayer plants', maranta: 'Prayer plants',
      stromanthe: 'Prayer plants', ctenanthe: 'Prayer plants', dischidia: 'Dischidia', orchid: 'Orchids', phalaenopsis: 'Orchids',
      dendrobium: 'Orchids', cattleya: 'Orchids', fern: 'Ferns', nephrolepis: 'Ferns', asplenium: 'Ferns', cactus: 'Cacti & succulents',
      mammillaria: 'Cacti & succulents', echeveria: 'Cacti & succulents', haworthia: 'Cacti & succulents', aloe: 'Cacti & succulents',
      pilea: 'Pilea', syngonium: 'Syngonium', scindapsus: 'Scindapsus', epipremnum: 'Epipremnum', aglaonema: 'Aglaonema',
      dracaena: 'Dracaena', sansevieria: 'Dracaena', zamioculcas: 'Zamioculcas', palm: 'Palms', chamaedorea: 'Palms', areca: 'Palms'
    };

    return known[first.toLowerCase()] || first.charAt(0).toUpperCase() + first.slice(1);
  }

  function petSafetyCategory(text) {
    var value = (text || '').toLowerCase();
    if (!value) return 'Not verified';
    if (/non[- ]?toxic|pet[- ]?safe|safe for cats|safe for dogs|aspca non/.test(value) && !/not\s+safe|toxic/.test(value)) return 'Pet-friendly / ASPCA non-toxic';
    if (/toxic|poison|calcium oxalate|irritat|sap|keep away|chew/.test(value)) return 'Toxic if chewed';
    if (/not verified|unknown|check exact|unconfirmed|not listed/.test(value)) return 'Not verified';
    return 'Keep away from chewers';
  }

  function normalizeSearch(text) {
    return String(text || '').toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
  }

  function makeSummary(node) {
    var fields = fieldMap(node.fields || []);
    var name = fieldValue(fields, ['plant_name', 'display_name', 'title', 'name']) || node.handle;
    var botanical = fieldValue(fields, ['accepted_botanical_name', 'botanical_name', 'scientific_name']);
    var profileHandle = fieldValue(fields, ['profile_handle', 'handle_key']) || node.handle;
    var commonNames = parseListValue(fieldValue(fields, ['common_names', 'common_name', 'aliases']));
    var categoryRaw = fieldValue(fields, ['plant_category_label', 'category_label', 'plant_group', 'plant_type', 'category']);
    var category = categoryRaw && categoryRaw.indexOf('gid://') === -1 ? categoryRaw : inferGroup(name, botanical);
    var petRaw = fieldValue(fields, ['quick_pet_safety', 'pet_safety', 'pet_safety_summary', 'full_pet_safety_guide']);

    var quick = {
      light: stripHtml(fieldValue(fields, ['quick_light', 'light', 'light_summary'])),
      watering: stripHtml(fieldValue(fields, ['quick_watering', 'watering', 'watering_summary'])),
      humidity: stripHtml(fieldValue(fields, ['quick_humidity', 'humidity', 'humidity_summary'])),
      temperature: stripHtml(fieldValue(fields, ['quick_temperature', 'temperature', 'temperature_summary'])),
      soil: stripHtml(fieldValue(fields, ['quick_soil', 'soil', 'soil_summary'])),
      petSafety: stripHtml(petRaw)
    };

    var searchText = normalizeSearch([name, botanical, profileHandle, node.handle, commonNames.join(' '), category, quick.petSafety].join(' '));

    return {
      id: node.id,
      handle: profileHandle,
      metaobjectHandle: node.handle,
      type: node.type,
      updatedAt: node.updatedAt,
      onlineStoreUrl: node.onlineStoreUrl,
      name: name,
      botanicalName: botanical,
      commonNames: commonNames,
      category: category,
      quick: quick,
      petSafetyCategory: petSafetyCategory(quick.petSafety),
      primaryProductIds: parseListValue(fieldValue(fields, ['primary_product', 'primary_products'])),
      heroImageIds: parseListValue(fieldValue(fields, ['hero_image', 'hero_images'])),
      commonProblemIds: parseListValue(fieldValue(fields, ['common_problems'])),
      fields: fields,
      searchText: searchText
    };
  }

  function makeReference(ref) {
    if (!ref) return null;
    var label = '';
    if (ref.__typename === 'Metaobject') {
      label = (ref.field && ref.field.value) || (ref.problemName && ref.problemName.value) || (ref.displayName && ref.displayName.value) || (ref.title && ref.title.value) || ref.handle;
      return { type: 'Metaobject', metaobjectType: ref.type, id: ref.id, handle: ref.handle, title: label, url: ref.onlineStoreUrl || '' };
    }
    if (ref.__typename === 'Product') {
      return {
        type: 'Product',
        id: ref.id,
        handle: ref.handle,
        title: ref.title,
        url: ref.onlineStoreUrl || (ref.handle ? '/products/' + ref.handle : ''),
        availableForSale: !!ref.availableForSale,
        featuredImage: ref.featuredImage || null,
        priceRange: ref.priceRange || null
      };
    }
    if (ref.__typename === 'MediaImage') {
      return { type: 'MediaImage', id: ref.id, image: ref.image || null };
    }
    return { type: ref.__typename || 'Reference' };
  }

  function makeDetail(node) {
    var summary = makeSummary(node);
    var fields = fieldMap(node.fields || []);
    var resolved = {};

    Object.keys(fields).forEach(function (key) {
      var field = fields[key];
      var references = [];
      if (field.reference) references.push(makeReference(field.reference));
      if (field.references && Array.isArray(field.references.nodes)) {
        references = references.concat(field.references.nodes.map(makeReference));
      }
      resolved[key] = references.filter(Boolean);
    });

    summary.resolved = resolved;
    summary.primaryProduct = (resolved.primary_product || resolved.primary_products || []).find(function (x) { return x.type === 'Product'; }) || null;
    summary.heroImage = (resolved.hero_image || resolved.hero_images || []).find(function (x) { return x.type === 'MediaImage'; }) || null;
    summary.commonProblems = (resolved.common_problems || []).filter(function (x) { return x.type === 'Metaobject'; });
    return summary;
  }

  function indexPlants(plants) {
    var byHandle = {};
    plants.forEach(function (plant) {
      byHandle[plant.handle] = plant;
      byHandle[plant.metaobjectHandle] = plant;
    });
    return byHandle;
  }

  function hydrate(plants, source) {
    memory.plants = plants.slice().sort(function (a, b) { return a.name.localeCompare(b.name); });
    memory.byHandle = indexPlants(memory.plants);
    memory.lastFetchedAt = Date.now();
    memory.source = source || 'api';
    window.dispatchEvent(new CustomEvent('emms:plant-catalogue:loaded', {
      detail: { count: memory.plants.length, source: memory.source, fetchedAt: memory.lastFetchedAt }
    }));
    return memory.plants;
  }

  function fetchAll(options) {
    options = options || {};
    if (memory.promise && !options.forceRefresh) return memory.promise;
    if (memory.plants && !options.forceRefresh) return Promise.resolve(memory.plants);

    var cached = !options.forceRefresh && readCache();
    if (cached) return Promise.resolve(hydrate(cached.plants, 'cache'));

    var all = [];
    var page = 0;

    function next(after) {
      page += 1;
      if (page > Number(config.maxPages || 30)) throw new Error('Catalogue pagination stopped at maxPages. Increase maxPages if the catalogue has grown.');
      window.dispatchEvent(new CustomEvent('emms:plant-catalogue:progress', { detail: { page: page, count: all.length } }));
      return graphQL(CATALOGUE_QUERY, {
        type: config.plantCoreType,
        first: Math.min(Number(config.pageSize || 250), 250),
        after: after || null
      }).then(function (data) {
        var connection = data && data.metaobjects;
        if (!connection) throw new Error('No metaobjects connection returned for ' + config.plantCoreType + '.');
        (connection.edges || []).forEach(function (edge) { if (edge.node) all.push(makeSummary(edge.node)); });
        if (connection.pageInfo && connection.pageInfo.hasNextPage) return next(connection.pageInfo.endCursor);
        writeCache(all);
        return hydrate(all, 'api');
      });
    }

    memory.promise = next(null).catch(function (err) {
      memory.promise = null;
      window.dispatchEvent(new CustomEvent('emms:plant-catalogue:error', { detail: { error: err } }));
      throw err;
    });

    return memory.promise;
  }

  function getAllPlants(options) {
    return fetchAll(options);
  }

  function getPlant(handle) {
    var safe = window.EmmsToolState ? window.EmmsToolState.normaliseHandle(handle) : String(handle || '').trim().toLowerCase();
    if (memory.byHandle && memory.byHandle[safe]) return Promise.resolve(memory.byHandle[safe]);
    return fetchAll().then(function () { return memory.byHandle[safe] || null; });
  }

  function getPlantDetail(handle, options) {
    options = options || {};
    var safe = window.EmmsToolState ? window.EmmsToolState.normaliseHandle(handle) : String(handle || '').trim().toLowerCase();
    if (!safe) return Promise.resolve(null);
    if (memory.detailPromises[safe] && !options.forceRefresh) return memory.detailPromises[safe];

    memory.detailPromises[safe] = graphQL(DETAIL_QUERY, {
      handle: { type: config.plantCoreType, handle: safe }
    }).then(function (data) {
      if (!data || !data.metaobject) return getPlant(safe);
      var detail = makeDetail(data.metaobject);
      if (memory.byHandle) {
        memory.byHandle[detail.handle] = detail;
        memory.byHandle[detail.metaobjectHandle] = detail;
      }
      return detail;
    });

    return memory.detailPromises[safe];
  }

  function filterPlants(filters) {
    filters = filters || {};
    var q = normalizeSearch(filters.query || '');
    var category = filters.category || '';
    var pet = filters.petSafetyCategory || filters.pet || '';
    var source = memory.plants || [];

    return source.filter(function (plant) {
      if (q && plant.searchText.indexOf(q) === -1) return false;
      if (category && category !== 'all' && plant.category !== category) return false;
      if (pet && pet !== 'all' && plant.petSafetyCategory !== pet) return false;
      return true;
    });
  }

  function categories() {
    var seen = {};
    (memory.plants || []).forEach(function (plant) { if (plant.category) seen[plant.category] = true; });
    return Object.keys(seen).sort(function (a, b) { return a.localeCompare(b); });
  }

  function diagnostics() {
    return {
      version: VERSION,
      config: Object.assign({}, config, { storefrontAccessToken: config.storefrontAccessToken ? '[set]' : '[missing]' }),
      count: memory.plants ? memory.plants.length : 0,
      source: memory.source,
      lastFetchedAt: memory.lastFetchedAt,
      cacheKey: cacheKey()
    };
  }

  window.EmmsPlantCatalogue = {
    version: VERSION,
    configure: configure,
    getAllPlants: getAllPlants,
    getPlant: getPlant,
    getPlantDetail: getPlantDetail,
    filterPlants: filterPlants,
    categories: categories,
    clearCache: clearCache,
    diagnostics: diagnostics,
    _normaliseSearch: normalizeSearch
  };
}());
